import Phaser from 'phaser';
import { GLYPH_SIZE } from '../toki/TokiPonaData';

// Performance timing helper (uses baseline from main.ts)
const _perf = (label: string) => {
    const fn = (window as any).__perf;
    if (fn) fn(label); else console.log(`[PERF] ${label}`);
};

// ============================================================
// TEXTURE MANAGER
// Pure pixel manipulation: hue shifting, transparency, glyph processing.
// No game logic dependencies.
// ============================================================

export interface ProcessedTextures {
    man_right: string;
    squat_man_right: string;
    leap_man_right: string;
    fall_man_right: string;
    man_hurt_right: string;
    man_booted_right: string;
    squat_man_booted_right: string;
    leap_man_booted_right: string;
    fall_man_booted_right: string;
    man_hurt_booted_right: string;
    dirt: string;
    stone: string;
    grass: string;
    portal: string;
    skull: string;
    stick: string;
    ladder: string;
    sword: string;
    slash: string;
    heart_full: string;
    heart_empty: string;
    log: string;
    trunk: string;
    leaves: string;
    opal: string;
    amethyst: string;
    turquoise: string;
    plank: string;
    shooting_star: string;
    mentor_right: string;
    lover_right: string;
    wooden_shield: string;
    stone_club: string;
    iron_sword: string;
    bubble_full: string;
    bubble_shrink1: string;
    bubble_shrink2: string;
    bubble_pop: string;
    hero_right: string;
    hero_squat_right: string;
    hero_leap_right: string;
    hero_fall_right: string;
    hero_noboots_right: string;
    hero_noboots_squat_right: string;
    hero_noboots_leap_right: string;
    hero_noboots_fall_right: string;
    boots: string;
    yellow_door_top: string;
    yellow_door_bottom: string;
    yellow_door_open_top: string;
    yellow_door_open_bottom: string;
    yellow_key: string;
    flint: string;
    red_stone: string;
    kiwen_anpa: string;
    kiwen_nimi: string;
    kiwen_seme: string;
    kiwen_seme_inert: string;
    kiwen_lukin: string;
    kiwen_lukin_active: string;
    kiwen_noka: string;
    kiwen_la: string;
    kiwen_la_active: string;
    kiwen_supa: string;
    kiwen_toki: string;
    kiwen_mi: string;
    kiwen_soweli: string;
    kiwen_ijo: string;
    kiwen_jan: string;
    kiwen_jelo: string;
    kiwen_jo: string;
    kiwen_kala: string;
    kiwen_kalama: string;
    kiwen_kama: string;
    kiwen_ko: string;
    kiwen_kulupu: string;
    kiwen_kute: string;
    kiwen_len: string;
    kiwen_lete: string;
    kiwen_li: string;
    kiwen_lipu: string;
    kiwen_luka: string;
    kiwen_mama: string;
    kiwen_mani: string;
    kiwen_moku: string;
    kiwen_moli: string;
    kiwen_nasin: string;
    kiwen_ni: string;
    kiwen_o: string;
    kiwen_olin: string;
    kiwen_ona: string;
    kiwen_pakala: string;
    kiwen_pan: string;
    kiwen_pana: string;
    kiwen_pi: string;
    kiwen_pimeja: string;
    kiwen_pipi: string;
    kiwen_poki: string;
    kiwen_pu: string;
    kiwen_seli: string;
    kiwen_sijelo: string;
    kiwen_suno: string;
    kiwen_tomo: string;
    kiwen_unpa: string;
    kiwen_wawa: string;
}

export interface PlayerTextures {
    man_right: string;
    squat_man_right: string;
    leap_man_right: string;
    fall_man_right: string;
    man_hurt_right: string;
    man_booted_right: string;
    squat_man_booted_right: string;
    leap_man_booted_right: string;
    fall_man_booted_right: string;
    man_hurt_booted_right: string;
}

export class TextureManager {
    private scene: Phaser.Scene;
    private atlasFrames: Set<string>;
    private playerTextureCache: Map<string, PlayerTextures> = new Map();
    processedTextures!: ProcessedTextures;

    static readonly ATLAS_KEY = 'game_atlas';

    constructor(scene: Phaser.Scene, atlasFrames: Set<string>) {
        this.scene = scene;
        this.atlasFrames = atlasFrames;
    }

    /**
     * Extract a single frame from the atlas into a standalone canvas.
     * Used for the few sprites that need pixel manipulation (doors, hue shifting, rodents).
     */
    extractAtlasFrame(frameName: string): HTMLCanvasElement | null {
        const atlas = this.scene.textures.get(TextureManager.ATLAS_KEY);
        if (!atlas) return null;
        const frame = atlas.get(frameName);
        if (!frame || frame.name === '__BASE') return null;
        const source = atlas.getSourceImage() as HTMLImageElement;
        const canvas = document.createElement('canvas');
        canvas.width = frame.cutWidth;
        canvas.height = frame.cutHeight;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(source, frame.cutX, frame.cutY, frame.cutWidth, frame.cutHeight, 0, 0, frame.cutWidth, frame.cutHeight);
        return canvas;
    }

    /**
     * Slice the top or bottom half from an atlas frame (8x16 -> 8x8) and register as standalone texture.
     */
    sliceAtlasFrame(frameName: string, half: 'top' | 'bottom', outputKey: string): string {
        const atlas = this.scene.textures.get(TextureManager.ATLAS_KEY);
        if (!atlas) return frameName;
        const frame = atlas.get(frameName);
        if (!frame || frame.name === '__BASE') return frameName;
        const source = atlas.getSourceImage() as HTMLImageElement;
        const halfH = frame.cutHeight / 2;
        const canvas = document.createElement('canvas');
        canvas.width = frame.cutWidth;
        canvas.height = halfH;
        const ctx = canvas.getContext('2d')!;
        const sy = half === 'top' ? frame.cutY : frame.cutY + halfH;
        ctx.drawImage(source, frame.cutX, sy, frame.cutWidth, halfH, 0, 0, frame.cutWidth, halfH);
        this.scene.textures.addCanvas(outputKey, canvas);
        return outputKey;
    }

    /**
     * Process all raw textures after preload completes.
     * Call this in create() after generating the portal texture.
     */
    processAll(): void {
        _perf('  TextureManager: atlas frame mapping START');
        // Atlas frames already have black pre-processed to transparent.
        // Just store the atlas frame name directly — no canvas extraction needed.
        // Door sprites need slicing (8x16 -> two 8x8 halves) as standalone textures.
        this.processedTextures = {
            // Player sprites — atlas frame names match directly
            man_right: 'man_right',
            squat_man_right: 'squat_man_right',
            leap_man_right: 'leap_man_right',
            fall_man_right: 'fall_man_right',
            man_booted_right: 'man_booted_right',
            squat_man_booted_right: 'squat_man_booted_right',
            leap_man_booted_right: 'leap_man_booted_right',
            fall_man_booted_right: 'fall_man_booted_right',
            man_hurt_booted_right: 'man_hurt_booted_right',
            man_hurt_right: 'man_hurt_right',
            // Block/tile sprites
            dirt: 'dirt',
            stone: 'stone',
            grass: 'grass',
            portal: 'portal',
            skull: 'skull',
            stick: 'stick',
            ladder: 'ladder',
            sword: 'sword',
            // Renamed atlas frames (atlas name differs from old texture key)
            slash: 'slash_projectile',
            heart_full: 'heart',
            heart_empty: 'heart_empty',
            log: 'log',
            trunk: 'trunk',
            leaves: 'leaves',
            opal: 'opal',
            amethyst: 'amethyst',
            turquoise: 'turquoise',
            plank: 'plank',
            shooting_star: 'shooting_star',
            mentor_right: 'mentor_right',
            lover_right: 'lover_right',
            // Generated textures (not from atlas)
            wooden_shield: 'wooden_shield_generated',
            stone_club: 'stone_club_generated',
            // iron_sword uses the same sprite as sword
            iron_sword: 'sword',
            // Bubble sprites (atlas frame names)
            bubble_full: 'bubble_big',
            bubble_shrink1: 'bubble_mid',
            bubble_shrink2: 'bubble_small',
            bubble_pop: 'bubble_pop',
            // Hero sprites
            hero_right: 'hero_right',
            hero_squat_right: 'hero_squat_right',
            hero_leap_right: 'hero_leap_right',
            hero_fall_right: 'hero_fall_right',
            hero_noboots_right: 'hero_noboots_right',
            hero_noboots_squat_right: 'hero_noboots_squat_right',
            hero_noboots_leap_right: 'hero_noboots_leap_right',
            hero_noboots_fall_right: 'hero_noboots_fall_right',
            // Items (atlas frame names)
            boots: 'boots',
            // Door sprites: extract from atlas, slice into top/bottom halves
            yellow_door_top: this.sliceAtlasFrame('yellow_door', 'top', 'yellow_door_top'),
            yellow_door_bottom: this.sliceAtlasFrame('yellow_door', 'bottom', 'yellow_door_bottom'),
            yellow_door_open_top: this.sliceAtlasFrame('yellow_door_open', 'top', 'yellow_door_open_top'),
            yellow_door_open_bottom: this.sliceAtlasFrame('yellow_door_open', 'bottom', 'yellow_door_open_bottom'),
            yellow_key: 'yellow_key',
            flint: 'flint',
            red_stone: 'red_stone',
            // Kiwen block sprites (atlas frame names)
            kiwen_anpa: 'kiwen_anpa',
            kiwen_nimi: 'kiwen_nimi',
            kiwen_seme: 'kiwen_seme',
            kiwen_seme_inert: 'kiwen_seme_inert',
            kiwen_lukin: 'kiwen_lukin',
            kiwen_lukin_active: 'kiwen_lukin_active',
            kiwen_noka: 'kiwen_noka',
            kiwen_la: 'kiwen_la',
            kiwen_la_active: 'kiwen_la_active',
            kiwen_supa: 'kiwen_supa',
            kiwen_toki: 'kiwen_toki',
            kiwen_mi: 'kiwen_mi',
            kiwen_soweli: 'kiwen_soweli',
            kiwen_ijo: 'kiwen_ijo',
            kiwen_jan: 'kiwen_jan',
            kiwen_jelo: 'kiwen_jelo',
            kiwen_jo: 'kiwen_jo',
            kiwen_kala: 'kiwen_kala',
            kiwen_kalama: 'kiwen_kalama',
            kiwen_kama: 'kiwen_kama',
            kiwen_ko: 'kiwen_ko',
            kiwen_kulupu: 'kiwen_kulupu',
            kiwen_kute: 'kiwen_kute',
            kiwen_len: 'kiwen_len',
            kiwen_lete: 'kiwen_lete',
            kiwen_li: 'kiwen_li',
            kiwen_lipu: 'kiwen_lipu',
            kiwen_luka: 'kiwen_luka',
            kiwen_mama: 'kiwen_mama',
            kiwen_mani: 'kiwen_mani',
            kiwen_moku: 'kiwen_moku',
            kiwen_moli: 'kiwen_moli',
            kiwen_nasin: 'kiwen_nasin',
            kiwen_ni: 'kiwen_ni',
            kiwen_o: 'kiwen_o',
            kiwen_olin: 'kiwen_olin',
            kiwen_ona: 'kiwen_ona',
            kiwen_pakala: 'kiwen_pakala',
            kiwen_pan: 'kiwen_pan',
            kiwen_pana: 'kiwen_pana',
            kiwen_pi: 'kiwen_pi',
            kiwen_pimeja: 'kiwen_pimeja',
            kiwen_pipi: 'kiwen_pipi',
            kiwen_poki: 'kiwen_poki',
            kiwen_pu: 'kiwen_pu',
            kiwen_seli: 'kiwen_seli',
            kiwen_sijelo: 'kiwen_sijelo',
            kiwen_suno: 'kiwen_suno',
            kiwen_tomo: 'kiwen_tomo',
            kiwen_unpa: 'kiwen_unpa',
            kiwen_wawa: 'kiwen_wawa',
        };

        _perf('  TextureManager: atlas frame mapping END');
        _perf('  TextureManager: processGlyphTexture START');
        this.processGlyphTexture();
        _perf('  TextureManager: processGlyphTexture END');
    }

    // Process glyph spritesheet: white bg -> transparent, black ink -> white
    // Creates a new spritesheet 'toki_glyphs' from 'toki_glyphs_raw'
    private processGlyphTexture(): void {
        const texture = this.scene.textures.get('toki_glyphs_raw');
        if (!texture) {
            console.warn('toki_glyphs_raw texture not found');
            return;
        }
        const source = texture.getSourceImage() as HTMLImageElement;

        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(source, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const threshold = 128; // midpoint split for 1-bit treatment

        for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2];
            const brightness = (r + g + b) / 3;
            if (brightness >= threshold) {
                // White/light pixel -> fully transparent
                data[i] = 0; data[i + 1] = 0; data[i + 2] = 0; data[i + 3] = 0;
            } else {
                // Black/dark pixel (ink) -> white, fully opaque
                data[i] = 255; data[i + 1] = 255; data[i + 2] = 255; data[i + 3] = 255;
            }
        }

        ctx.putImageData(imageData, 0, 0);

        // Create as a spritesheet with same frame dimensions
        // Cast: Phaser runtime accepts canvas but TS types only declare HTMLImageElement
        this.scene.textures.addSpriteSheet(
            'toki_glyphs',
            canvas as unknown as HTMLImageElement,
            { frameWidth: GLYPH_SIZE, frameHeight: GLYPH_SIZE }
        );
    }

    // Process a texture to make pure black (0x000000) pixels transparent
    // Returns the new texture key
    makeBlackTransparent(textureKey: string, outputKey?: string): string {
        // Atlas-aware fast path: if key is an atlas frame, black is already transparent.
        // Extract to canvas and register as standalone texture (needed for pixel manipulation).
        if (this.atlasFrames.has(textureKey)) {
            const processedKey = outputKey || (textureKey + '_processed');
            const canvas = this.extractAtlasFrame(textureKey);
            if (canvas) {
                if (this.scene.textures.exists(processedKey)) {
                    this.scene.textures.remove(processedKey);
                }
                this.scene.textures.addCanvas(processedKey, canvas);
                return processedKey;
            }
            return textureKey;
        }

        if (!this.scene.textures.exists(textureKey)) {
            console.warn(`Texture ${textureKey} not found for makeBlackTransparent`);
            return textureKey;
        }
        const texture = this.scene.textures.get(textureKey);
        const source = texture.getSourceImage() as HTMLImageElement | HTMLCanvasElement;
        const processedKey = outputKey || (textureKey + '_processed');

        // Fast path: if source is a canvas (already pre-processed),
        // just re-register it under the new key — no pixel processing needed
        if (source instanceof HTMLCanvasElement) {
            if (processedKey !== textureKey) {
                if (this.scene.textures.exists(processedKey)) {
                    this.scene.textures.remove(processedKey);
                }
                this.scene.textures.addCanvas(processedKey, source);
            }
            return processedKey;
        }

        // Slow path: HTMLImageElement (non-atlas sprites, spritesheets)
        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(source, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const threshold = 10;

        for (let i = 0; i < data.length; i += 4) {
            if (data[i] <= threshold && data[i + 1] <= threshold && data[i + 2] <= threshold) {
                data[i + 3] = 0;
            }
        }

        ctx.putImageData(imageData, 0, 0);
        this.scene.textures.addCanvas(processedKey, canvas);
        return processedKey;
    }

    // RGB to HSL conversion
    private rgbToHsl(r: number, g: number, b: number): [number, number, number] {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s = 0;
        const l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                case g: h = ((b - r) / d + 2) / 6; break;
                case b: h = ((r - g) / d + 4) / 6; break;
            }
        }
        return [h, s, l];
    }

    // HSL to RGB conversion
    private hslToRgb(h: number, s: number, l: number): [number, number, number] {
        let r, g, b;
        if (s === 0) {
            r = g = b = l;
        } else {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1/6) return p + (q - p) * 6 * t;
                if (t < 1/2) return q;
                if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
                return p;
            };
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
        }
        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    }

    // Create a hue-shifted version of a texture (only shifts saturated pixels)
    // hueShift is 0-1 (fraction of full rotation)
    createHueShiftedTexture(baseTextureKey: string, hueShift: number, newKey: string): string {
        // Atlas-aware: extract frame to canvas first if it's an atlas frame
        let source: HTMLCanvasElement | HTMLImageElement;
        if (this.atlasFrames.has(baseTextureKey)) {
            const extracted = this.extractAtlasFrame(baseTextureKey);
            if (!extracted) {
                console.warn(`Atlas frame ${baseTextureKey} not found for hue shift`);
                return baseTextureKey;
            }
            source = extracted;
        } else {
            const texture = this.scene.textures.get(baseTextureKey);
            if (!texture) {
                console.warn(`Texture ${baseTextureKey} not found for hue shift`);
                return baseTextureKey;
            }
            source = texture.getSourceImage() as HTMLCanvasElement;
        }

        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(source, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Saturation threshold - pixels with saturation below this are considered greyscale
        const saturationThreshold = 0.15;

        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            // Skip transparent pixels
            if (a === 0) continue;

            const [h, s, l] = this.rgbToHsl(r, g, b);

            // Only shift hue for saturated pixels (not greyscale)
            if (s > saturationThreshold) {
                const newH = (h + hueShift) % 1;
                const [newR, newG, newB] = this.hslToRgb(newH, s, l);
                data[i] = newR;
                data[i + 1] = newG;
                data[i + 2] = newB;
            }
            // Greyscale pixels (low saturation) remain unchanged
        }

        ctx.putImageData(imageData, 0, 0);
        this.scene.textures.addCanvas(newKey, canvas);
        return newKey;
    }

    // Create a desaturated (greyscale) version of a texture
    // Used for empty hearts
    createDesaturatedTexture(baseTextureKey: string, newKey: string): string {
        // Atlas-aware: extract frame to canvas first if it's an atlas frame
        let source: HTMLCanvasElement | HTMLImageElement;
        if (this.atlasFrames.has(baseTextureKey)) {
            const extracted = this.extractAtlasFrame(baseTextureKey);
            if (!extracted) {
                console.warn(`Atlas frame ${baseTextureKey} not found for desaturation`);
                return baseTextureKey;
            }
            source = extracted;
        } else {
            const texture = this.scene.textures.get(baseTextureKey);
            if (!texture) {
                console.warn(`Texture ${baseTextureKey} not found for desaturation`);
                return baseTextureKey;
            }
            source = texture.getSourceImage() as HTMLCanvasElement;
        }

        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(source, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            // Skip transparent pixels
            if (a === 0) continue;

            // Convert to greyscale (luminance formula)
            const grey = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
            // Make it darker for empty heart look
            const darkGrey = Math.round(grey * 0.4);
            data[i] = darkGrey;
            data[i + 1] = darkGrey;
            data[i + 2] = darkGrey;
        }

        ctx.putImageData(imageData, 0, 0);
        this.scene.textures.addCanvas(newKey, canvas);
        return newKey;
    }

    // Get a deterministic hue shift for a player based on their ID
    getPlayerHueShift(playerId: string): number {
        // Hash the player ID to get a consistent hue shift
        let hash = 0;
        for (let i = 0; i < playerId.length; i++) {
            const char = playerId.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        // Convert to 0-1 range, avoiding values too close to 0 (original color)
        // Use golden ratio for good distribution
        const goldenRatio = 0.618033988749895;
        return ((Math.abs(hash) * goldenRatio) % 1);
    }

    // Get or create hue-shifted textures for a player
    getPlayerTextures(playerId: string): PlayerTextures {
        // Check if we already created textures for this player
        if (this.playerTextureCache.has(playerId)) {
            return this.playerTextureCache.get(playerId)!;
        }

        const hueShift = this.getPlayerHueShift(playerId);
        const suffix = `_player_${playerId}`;

        const textures: PlayerTextures = {
            man_right: this.createHueShiftedTexture(this.processedTextures.man_right, hueShift, `man_right${suffix}`),
            squat_man_right: this.createHueShiftedTexture(this.processedTextures.squat_man_right, hueShift, `squat_man_right${suffix}`),
            leap_man_right: this.createHueShiftedTexture(this.processedTextures.leap_man_right, hueShift, `leap_man_right${suffix}`),
            fall_man_right: this.createHueShiftedTexture(this.processedTextures.fall_man_right, hueShift, `fall_man_right${suffix}`),
            man_hurt_right: this.createHueShiftedTexture(this.processedTextures.man_hurt_right, hueShift, `man_hurt_right${suffix}`),
            man_booted_right: this.createHueShiftedTexture(this.processedTextures.man_booted_right, hueShift, `man_booted_right${suffix}`),
            squat_man_booted_right: this.createHueShiftedTexture(this.processedTextures.squat_man_booted_right, hueShift, `squat_man_booted_right${suffix}`),
            leap_man_booted_right: this.createHueShiftedTexture(this.processedTextures.leap_man_booted_right, hueShift, `leap_man_booted_right${suffix}`),
            fall_man_booted_right: this.createHueShiftedTexture(this.processedTextures.fall_man_booted_right, hueShift, `fall_man_booted_right${suffix}`),
            man_hurt_booted_right: this.createHueShiftedTexture(this.processedTextures.man_hurt_booted_right, hueShift, `man_hurt_booted_right${suffix}`),
        };

        this.playerTextureCache.set(playerId, textures);
        return textures;
    }

    // Remove cached textures for a player (on disconnect)
    removePlayerTextures(playerId: string): void {
        const textures = this.playerTextureCache.get(playerId);
        if (textures) {
            this.scene.textures.remove(textures.man_right);
            this.scene.textures.remove(textures.squat_man_right);
            this.scene.textures.remove(textures.leap_man_right);
            this.scene.textures.remove(textures.fall_man_right);
            this.playerTextureCache.delete(playerId);
        }
    }
}
